import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, RotateCcw, Orbit, ArrowRight } from "lucide-react";
import PublicShell, { WIDE } from "@/components/PublicShell";
import { api } from "@/lib/apiClient";
import { usePageMeta } from "@/lib/seo";
import { PLANET_PAGES } from "@/lib/planetFacts";
import RetroBanner from "@/components/RetroBanner";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
const wrap = `px-5 pt-6 pb-10 fade-up ${WIDE ? "lg:max-w-3xl lg:mx-auto lg:pt-12" : ""}`;

function BodyCard({ b }) {
  const rows = [
    ["Sign", `${b.planet} in ${b.sign}`],
    ["Position", `${b.degree}°${String(b.minute).padStart(2, "0")}′ ${b.sign}`],
    ["Motion", b.retrograde ? "Retrograde" : "Direct"],
    b.phase && ["Moon phase", `${b.phase} · ${b.illumination}% lit`],
    ["Distance", b.distance_km ? `${b.distance_km.toLocaleString()} km` : `${b.distance_au} AU`],
    b.next_sign && ["Next sign", `${b.next_sign}, ${fmtDate(b.next_sign_at)}`],
  ].filter(Boolean);
  return (
    <div data-testid={`transit-body-${b.planet.toLowerCase()}`} className="bg-[#0F1523]/90 border border-[#D4AF37]/25 rounded-2xl p-5">
      <p className="text-2xl font-light mb-3">{b.planet} in <span className="gold-text">{b.sign}</span>{b.retrograde && <span className="ml-2 inline-flex items-center gap-1 text-xs text-[#E8DCC4] border border-[#D4AF37]/40 rounded-full px-2 py-0.5 align-middle"><RotateCcw size={11} /> Rx</span>}</p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {rows.map(([k, v]) => <div key={k}><dt className="text-[11px] uppercase tracking-[0.2em] text-[#A7B0C0]">{k}</dt><dd className="text-[#E8DCC4]">{v}</dd></div>)}
      </dl>
    </div>
  );
}

function OtherPlanets({ current }) {
  return (
    <nav aria-label="Other planets today" data-testid="transit-other-planets" className="mt-8 flex flex-wrap gap-2">
      {Object.entries(PLANET_PAGES).filter(([s]) => s !== current).map(([s, p]) => (
        <Link key={s} to={`/transits/${s}`} data-testid={`transit-link-${s}`} className="text-sm border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 text-[#E8DCC4] rounded-full px-4 py-2 transition-colors">{p.name} today</Link>
      ))}
      <Link to="/transits" data-testid="transit-link-all" className="text-sm gold-text px-2 py-2">All planets</Link>
    </nav>
  );
}

export default function TransitPage() {
  const { planet } = useParams();
  const { user } = useAuth();
  const facts = PLANET_PAGES[planet];
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  usePageMeta(facts ? `${facts.title} Live ${facts.name} position · Astra` : "Planet not found · Astra", `/transits/${planet}`,
    facts && `Live ${facts.name} position today: sign, degree, retrograde status and next sign change, calculated with the PyEphem ephemeris, plus what it means for you.`);
  useEffect(() => {
    if (!facts) return;
    setD(null); setError(false);
    api.get(`/public/transits/${planet}`).then(({ data }) => setD(data)).catch(() => setError(true));
  }, [planet, facts]);

  if (!facts) return <PublicShell testId="transit-page"><p data-testid="transit-not-found" className="text-center text-[#A7B0C0] py-24">This planet page doesn't exist.</p></PublicShell>;
  return (
    <PublicShell testId="transit-page">
      <article className={wrap}>
        <p className="text-[11px] uppercase tracking-[0.3em] gold-text"><Orbit size={11} className="inline -mt-0.5 mr-1" />Daily planet · {d ? fmtDate(d.updated_at) : "today"}</p>
        <h1 data-testid="transit-title" className="text-4xl sm:text-5xl font-light mt-2 mb-6"><span className="gold-text mr-2">{facts.glyph}</span>{facts.title}</h1>
        {!["sun", "moon"].includes(planet) && <RetroBanner only={planet === "uranus-neptune" ? ["Uranus", "Neptune"] : [facts.name]} className="mb-4" />}
        {error && <p data-testid="transit-error" className="text-[#A7B0C0]">Live positions are taking a moment to load. Please refresh in a minute.</p>}
        {!d && !error && <div className="flex justify-center py-12"><Loader2 className="animate-spin gold-text" size={26} /></div>}
        {d && (
          <>
            <div className={`grid gap-3 ${d.bodies.length > 1 ? "sm:grid-cols-2" : ""}`}>{d.bodies.map((b) => <BodyCard key={b.planet} b={b} />)}</div>
            {d.note && (
              <section data-testid="transit-note" className="mt-6">
                <h2 className="text-xl font-light mb-2">What it means today</h2>
                {d.note.split(/\n+/).map((p, i) => <p key={i} className="text-base leading-relaxed text-[#D6D0C5] mb-3">{p}</p>)}
              </section>
            )}
          </>
        )}
        <section data-testid="transit-about" className="mt-6 bg-[#0F1523]/70 border border-[#D4AF37]/15 rounded-2xl p-5">
          <h2 className="text-xl font-light mb-2">About {facts.name} in astrology</h2>
          <p className="text-base leading-relaxed text-[#D6D0C5]">{facts.about}</p>
          <p className="text-sm text-[#A7B0C0] mt-3">{facts.rhythm}</p>
          <p className="text-xs text-[#A7B0C0] mt-3">Positions are calculated live with the PyEphem / VSOP87 ephemeris (tropical zodiac, geocentric). <Link to="/editorial-policy" className="gold-text">How we create content</Link></p>
        </section>
        <Link to={user ? "/chart" : "/join"} data-testid="transit-cta" className="mt-6 flex items-center justify-between bg-[#D4AF37] hover:bg-[#e6c04e] text-[#04060B] font-medium rounded-full px-6 py-3.5 transition-colors">
          {user ? "Open your birth chart" : `See what ${facts.name} means for your birth chart`} <ArrowRight size={17} />
        </Link>
        <OtherPlanets current={planet} />
      </article>
    </PublicShell>
  );
}
